import React, { useEffect } from "react";
import SplashScreen from "./components/SplashScreen";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
} from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { useAuth } from "./context/AuthContext";
import { supabase, initialAuthLink } from "./supabase/supabase";
import { hideNativeSplash } from "./capacitor/splash";
import { syncStatusBarStyle } from "./capacitor/statusBar";
import { useTheme } from "./hooks/useTheme";

import SignUp from "./pages/SignUp";
import VerifyEmail from "./pages/VerifyEmail";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import CreateLeague from "./pages/CreateLeague";
import JoinLeague from "./pages/JoinLeague";
import DraftRoom from "./pages/DraftRoom.jsx";
import Home from "./pages/Home";
import Landing from "./pages/Landing";
import MailingListConfirm from "./pages/MailingListConfirm";
import MyLineup from "./pages/MyLineup";
import LeagueTab from "./pages/LeagueTab";
import TeamsTab from "./pages/TeamsTab";
import ScoreboardTab from "./pages/ScoreboardTab";
import DraftGuard from "./components/DraftGuard";
import HowToPlay from "./pages/HowToPlay";
import Scouting from "./pages/Scouting";
import OldPathRedirect from "./components/OldPathRedirect";
import PrivateRoute from "./components/PrivateRoute";
import HigherLowerGame from "./pages/games/HigherLowerGame";
import HomeFieldGame from "./pages/games/HomeFieldGame";
import PickThreeGame from "./pages/games/PickThreeGame";
import PickThreePublic from "./pages/PickThreePublic";
import UpdateGate from "./components/UpdateGate";
import LeagueLayout from "./components/LeagueLayout";
import AdminPanel from "./pages/AdminPanel";
import AdminLeagueDetail from "./pages/AdminLeagueDetail";
import AdminTeamsPanel from "./pages/AdminTeamsPanel";
import AdminSchedulePanel from "./pages/AdminSchedulePanel";
import Privacy from "./pages/legal/Privacy";
import Terms from "./pages/legal/Terms";
import Support from "./pages/legal/Support";
import DeleteAccount from "./pages/legal/DeleteAccount";

// A recovery link is worth exactly one redirect per page load. Returns true to
// the first caller that finds one, false to every caller after it.
let recoveryRedirectClaimed = false;

function claimRecoveryRedirect() {
  if (recoveryRedirectClaimed) return false;
  const fromRecoveryLink =
    initialAuthLink.isRecovery || initialAuthLink.errorCode === "otp_expired";
  if (!fromRecoveryLink) return false;
  recoveryRedirectClaimed = true;
  return true;
}

function AppWrapper() {
  return (
    <Router>
      {/* Outside the router's routes but inside it, so the gate can read the
          current path and still let the legal pages through. */}
      <UpdateGate>
        <App />
      </UpdateGate>
    </Router>
  );
}

// The animated splash (shimmer wordmark, pulsing dots, rotating status line)
// needs a few seconds on screen to read as intentional rather than a
// flicker, and long enough to show its three rotating status lines (1000ms
// each in SplashScreen.js) at least once. A cached session resolves auth in
// well under that, so the React splash is held for this floor even after
// authLoading clears.
const SPLASH_MIN_MS = 3000;

// A browser opening the bare site root gets no splash at all. That is the
// landing page, a stranger's first look, and three seconds of branded loading
// before a page with two buttons on it read as the site being slow. The apps
// keep it: they also start at "/", where the splash is their launch screen for
// signed-in users on their way to /home. Read once at boot, since App mounts
// once and later navigation to "/" never shows the splash anyway.
const SKIP_SPLASH = !Capacitor.isNativePlatform() && window.location.pathname === "/";

function App() {
  const { loading: authLoading } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [splashMinElapsed, setSplashMinElapsed] = React.useState(false);

  // Runs from first paint, so the dark splash gets light-on-dark status bar
  // icons even before auth resolves, not just once the app itself is up.
  useEffect(() => {
    syncStatusBarStyle(theme);
  }, [theme]);

  // The native splash is configured not to autohide, so it covers the gap
  // between launch and the first React paint. Taking it down here rather than
  // on a timer means it lasts exactly as long as auth takes; the React splash
  // that replaces it then holds for SPLASH_MIN_MS on its own timer below, so
  // the native-to-React handoff stays instant even though the branded splash
  // lingers.
  useEffect(() => {
    if (!authLoading) hideNativeSplash();
  }, [authLoading]);

  useEffect(() => {
    const id = setTimeout(() => setSplashMinElapsed(true), SPLASH_MIN_MS);
    return () => clearTimeout(id);
  }, []);

  // A recovery link can land on any route, so route it to the reset form from
  // wherever it arrives. Signup verification uses a code rather than a link, so
  // an expired-link error is treated as a failed recovery too — /reset-password
  // explains it and offers a fresh link.
  //
  // The redirect is claimed rather than merely performed: `useNavigate` returns
  // a fresh identity on every location change, so this effect re-runs on each
  // navigation, and the link details describe the page load rather than the
  // current route. Without the claim, every attempt to leave /reset-password —
  // including the redirect after a successful save — bounced straight back.
  useEffect(() => {
    if (!claimRecoveryRedirect()) return;
    if (window.location.pathname !== "/reset-password") {
      navigate("/reset-password", { replace: true });
    }
  }, [navigate]);

  // Backstop: fires when the client exchanges a recovery token, covering links
  // whose fragment was already consumed before the check above could read it.
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        navigate("/reset-password", { replace: true });
      }
    });
    return () => sub.subscription.unsubscribe();
  }, [navigate]);

  if (SKIP_SPLASH) {
    // The auth check still has to finish before Landing can decide between
    // itself and /home. It takes a moment, so hold an empty page in the
    // landing page's own colour rather than the splash.
    if (authLoading) return <div className="theme-auto" style={{ minHeight: "100vh", backgroundColor: "var(--color-bg-card)" }} />;
  } else if (authLoading || !splashMinElapsed) {
    return <SplashScreen />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/home" element={<PrivateRoute><Home /></PrivateRoute>} />
      <Route path="/create-league" element={<PrivateRoute><CreateLeague /></PrivateRoute>} />
      <Route path="/join-league" element={<PrivateRoute><JoinLeague /></PrivateRoute>} />
      <Route path="/join/:code" element={<PrivateRoute><JoinLeague /></PrivateRoute>} />
      <Route path="/how-to-play" element={<HowToPlay />} />
      <Route path="/demo" element={<Home demo />} />
      {/* The daily games on /home. Accounts only: the demo shows the cards
          and sends a visitor to sign up. No league is needed. */}
      <Route path="/games/higher-lower" element={<PrivateRoute><HigherLowerGame /></PrivateRoute>} />
      <Route path="/games/home-field" element={<PrivateRoute><HomeFieldGame /></PrivateRoute>} />
      {/* The weekly game's front door: open to anyone, no account, so an advert
          or a shared score can point at it. Picks are chosen here and saved to
          the account after sign up. A signed-in visitor is sent on to the real
          picker. */}
      <Route path="/pick-three" element={<PickThreePublic />} />
      {/* The weekly game. Accounts only: picks are stored per user. */}
      <Route path="/games/pick-three" element={<PrivateRoute><PickThreeGame /></PrivateRoute>} />

      {/* Public and unauthenticated: App Store review opens these directly. */}
      <Route path="/mailing-list/:action" element={<MailingListConfirm />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/support" element={<Support />} />
      <Route path="/delete-account" element={<DeleteAccount />} />
      <Route path="/admin" element={<AdminPanel />} />
      <Route path="/admin/league/:leagueId" element={<AdminLeagueDetail />} />
      <Route path="/admin/teams" element={<AdminTeamsPanel />} />
      <Route path="/admin/schedule" element={<AdminSchedulePanel />} />

      {/* League Pages — all share LeagueProvider + DraftProvider via LeagueLayout.
          Not wrapped in PrivateRoute, so a signed-out visitor can open the
          public demo league. RLS is the gate: for anon every other league
          loads as unavailable, and LeagueLayout sends them to /login. */}
      <Route path=":leagueId" element={<LeagueLayout />}>
        <Route path="draft-room" element={<DraftRoom />} />
        <Route path="scouting" element={<Scouting />} />

        {/* New tab routes. League is reachable pre-draft (it's in the 3-tab
            pre-draft set), the other three are post-draft only. */}
        <Route path="league" element={<LeagueTab />} />
        <Route path="lineup" element={<DraftGuard><MyLineup /></DraftGuard>} />
        <Route path="scoreboard" element={<DraftGuard><ScoreboardTab /></DraftGuard>} />
        <Route path="teams" element={<DraftGuard><TeamsTab /></DraftGuard>} />

        {/* Old paths — redirect for bookmarks and stale mobile builds. */}
        <Route path="my-lineup" element={<OldPathRedirect to="lineup" />} />
        <Route path="free-agents" element={<OldPathRedirect to="teams" />} />
        <Route path="stats" element={<OldPathRedirect to="teams" />} />
        <Route path="my-league" element={<OldPathRedirect to="league" />} />
        <Route path="members" element={<OldPathRedirect to="league" />} />
        <Route path="league-rules" element={<OldPathRedirect to="league" />} />
      </Route>
    </Routes>
  );
}

export default AppWrapper;
